import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as TooltipUI } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { GRID_CONFIG } from '@/lib/chartConfig';
import { formatTrendsXAxisLabel, formatXAxisLabel } from '@/lib/utils';
import { WidgetData } from '@/types';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  Line,
  LineChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface CallVolumeTrendLineChartProps {
  data: WidgetData;
  isGeneralMode?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

interface TooltipPayloadItem {
  value: number;
  dataKey: 'totalInboundCalls' | 'totalOutboundCalls' | 'totalInboundDirectCalls' | 'totalInternalCalls';
  name: string;
  color: string;
  payload: {
    time: string;
    totalInboundCalls: number;
    totalOutboundCalls: number;
    totalInboundDirectCalls: number;
    totalInternalCalls: number;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  theme?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

interface ChartContentProps {
  data: Array<{
    time: string;
    totalInboundCalls: number;
    totalOutboundCalls: number;
    totalInboundDirectCalls: number;
    totalInternalCalls: number;
  }>;
  theme: string;
  rangeType: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

const CustomTooltip = ({ active, payload, label, theme, rangeType }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className={`border rounded-lg shadow-md p-3 min-w-[220px] ${theme === "dark"
        ? "bg-gray-800 border-gray-600 text-white"
        : "bg-white border-gray-200 text-gray-900"
        }`}>
        <div className="font-semibold mb-2">{formatTrendsXAxisLabel(label, rangeType)}</div>
        {payload.map((item) => (
          <div key={item.dataKey} className="text-sm flex justify-between items-center" style={{ color: item.color }}>
            <span>{item.name}:</span>
            <span className="font-medium ml-2">{item.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Colors for different call types
const COLORS = {
  totalInboundCalls: '#23407A',
  totalOutboundCalls: '#00FEC1',
  totalInboundDirectCalls: '#FFB300',
  totalInternalCalls: '#8E44AD'
};

const DARK_COLORS = {
  totalInboundCalls: '#1681F6',
  totalOutboundCalls: '#00FEC1',
  totalInboundDirectCalls: '#FFB300',
  totalInternalCalls: '#8E44AD'
};
const ChartContent = ({ data, theme, rangeType }: ChartContentProps) => {
  // Check if all data points are 0
  const isAllDataZero = data?.every(item =>
    item.totalInboundCalls === 0 &&
    item.totalOutboundCalls === 0 &&
    item.totalInboundDirectCalls === 0 &&
    item.totalInternalCalls === 0
  ) ?? false;

  const colors = theme === "dark" ? DARK_COLORS : COLORS;

  const getXDomain = (data => {
    if (isAllDataZero) {
      return [0, 2, 4, 6, 8, 10]
    }
    // Find the maximum value among all call types in the data
    const maxVal = Math.max(
      ...data.flatMap(item => [
        item.totalInboundCalls,
        item.totalOutboundCalls,
        item.totalInboundDirectCalls,
        item.totalInternalCalls
      ])
    );
    if (maxVal <= 10) {
      // For data lower than or equal to 10, use ticks [0, 2, 4, 6, 8, 10]
      return [0, 2, 4, 6, 8, 10];
    }
    if (maxVal > 1000) {
      // 0, 1000, 2000, ..., up to the next thousand above maxVal
      const upper = Math.ceil(maxVal / 1000) * 1000;
      const ticks = [];
      for (let i = 0; i <= upper; i += 1000) {
        ticks.push(i);
      }
      return ticks;
    } else if (maxVal > 100) {
      // 0, 100, 200, ..., up to the next hundred above maxVal
      const upper = Math.ceil(maxVal / 100) * 100;
      const ticks = [];
      for (let i = 0; i <= upper; i += 100) {
        ticks.push(i);
      }
      return ticks;
    } else if (maxVal > 10) {
      // 0, 10, 20, ..., up to the next ten above maxVal
      const upper = Math.ceil(maxVal / 10) * 10;
      const ticks = [];
      for (let i = 0; i <= upper; i += 10) {
        ticks.push(i);
      }
      return ticks;
    } else {
      return [0, 10];
    }
  })
  return (
    <ResponsiveContainer width="99%" height={280} key={`chart-container-${rangeType}`}>
      <LineChart
        data={data}
        margin={{
          left: 20,
          right: 30,
          top: 20,
          bottom: rangeType === 'monthly' ? 10 : 10
        }}
        key={`line-chart-${rangeType}`}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          tickFormatter={(label) => formatTrendsXAxisLabel(label, rangeType)}
          tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
        />
        <YAxis
          tick={{ fill: theme === "dark" ? '#fff' : '#6b7280' }}
          domain={getXDomain(data)}
          ticks={getXDomain(data)}
        />
        <Tooltip content={<CustomTooltip theme={theme} rangeType={rangeType} />} cursor={{ stroke: theme === "dark" ? '#4b5563' : '#d1d5db' }} />
        <Legend
          verticalAlign="top"
          align="center"
          wrapperStyle={{ fontSize: 14, paddingBottom: 10 }}
          iconType="line"
          formatter={(value) => <span className='text-[14px]'>{value}</span>}
        />
        {!isAllDataZero && (
          <>
            <Line
              type="monotone"
              dataKey="totalInboundCalls"
              name="Inbound"
              stroke={colors.totalInboundCalls}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="totalOutboundCalls"
              name="Outbound"
              stroke={colors.totalOutboundCalls}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="totalInboundDirectCalls"
              name="Inbound Direct"
              stroke={colors.totalInboundDirectCalls}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="totalInternalCalls"
              name="Internal"
              stroke={colors.totalInternalCalls}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </>
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

const CallVolumeTrendLineChart: React.FC<CallVolumeTrendLineChartProps> = ({
  data,
  icon,
  loading,
  error,
  rangeType = 'monthly'
}) => {
  const { theme } = useDashboard();

  const chartData = useMemo(() => {
    const rawData = data.value;
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return [];
    }
    return rawData;
  }, [data.value]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton className="w-full h-[300px] rounded-none" />;
    }

    if (error) {
      return <div className="text-red-500 text-center py-10">{error}</div>;
    }

    if (!Array.isArray(chartData) || chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
          <div className="flex flex-col items-center gap-2">
            <span>No call records were found</span>
          </div>
        </div>
      );
    }

    return <ChartContent data={chartData} theme={theme} rangeType={rangeType} />;
  };

  return (
    <Card className="w-full overflow-hidden min-h-[300px] dark:border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <span>Call Volume Trend</span>
          <TooltipProvider>
            <TooltipUI delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="cursor-pointer">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="max-w-xs text-xs font-normal">
                  The pattern of call direction within the given time.
                </p>
              </TooltipContent>
            </TooltipUI>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          {renderContent()}
        </div>
      </CardContent>
    </Card>
  );
};

export default CallVolumeTrendLineChart;